import { Component, input, output, OnInit, OnDestroy, signal, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { ToBanglaNumberPipe } from '../../../../shared/pipes/toBanglaNumber.pipe';

export interface LoaderStage {
  title: string;
  description: string;
  progress: string;
  step: number; // -1 for intro, 0..6 for the 7 criteria steps, 7 for completed
  footer: string;
  targetPercent: number;
}

export interface StepItem {
  label: string;
  labelBn: string;
  icon: string;
}

@Component({
  selector: 'app-ai-matching-loader',
  standalone: true,
  imports: [TranslocoModule, ToBanglaNumberPipe],
  templateUrl: './ai-matching-loader.html',
  styleUrl: './ai-matching-loader.scss'
})
export class AiMatchingLoaderComponent implements OnInit, OnDestroy {
  private platformId = inject(PLATFORM_ID);
  private translocoService = inject(TranslocoService);
  private isBrowser = isPlatformBrowser(this.platformId);

  isApiComplete = input<boolean>(false);
  completed = output<void>();

  progressPercent = signal<number>(0);
  currentStageIndex = signal<number>(0);
  animatingCopy = signal<boolean>(true);

  get currentLang(): string {
    return this.translocoService.getActiveLang();
  }

  readonly stagesEn: LoaderStage[] = [
    {
      title: 'Initializing AI',
      description: 'Getting everything ready for your personalized match assessment.',
      progress: 'Getting things ready',
      step: -1,
      footer: 'A little analysis. A clearer picture.',
      targetPercent: 0
    },
    {
      title: 'Reading your profile',
      description: 'Bringing your qualifications and career preferences together.',
      progress: 'Understanding your profile',
      step: -1,
      footer: 'Every detail helps us see the fit.',
      targetPercent: 10
    },
    {
      title: 'Checking education',
      description: 'Comparing your qualifications with the education this role needs.',
      progress: 'Reviewing qualifications',
      step: 0,
      footer: 'Your first insights are on the way.',
      targetPercent: 22
    },
    {
      title: 'Checking your location',
      description: 'Comparing the job location with your preferences.',
      progress: 'Checking location',
      step: 1,
      footer: 'Your assessment is taking shape.',
      targetPercent: 35
    },
    {
      title: 'Comparing salary',
      description: 'Checking how the salary aligns with your expectations.',
      progress: 'Reviewing expected salary',
      step: 2,
      footer: 'The practical details matter.',
      targetPercent: 48
    },
    {
      title: 'Reviewing experience',
      description: 'Comparing your work history with the requirements for this role.',
      progress: 'Reviewing experience',
      step: 3,
      footer: 'Looking beyond the job title.',
      targetPercent: 62
    },
    {
      title: 'Checking industry fit',
      description: 'Exploring how your industry background aligns.',
      progress: 'Checking industry alignment',
      step: 4,
      footer: 'Putting your experience in context.',
      targetPercent: 74
    },
    {
      title: 'Matching your skills',
      description: 'Finding the overlap between your skills and this role.',
      progress: 'Connecting your strengths',
      step: 5,
      footer: 'Your strengths are taking shape.',
      targetPercent: 84
    },
    {
      title: 'Calculating your match',
      description: 'Bringing your match insights together.',
      progress: 'Finalizing your match',
      step: 6,
      footer: 'Your final match is almost ready.',
      targetPercent: 93
    },
    {
      title: 'Your assessment is ready',
      description: 'Here’s how your profile matches this opportunity.',
      progress: 'Assessment complete',
      step: 7,
      footer: 'Your match, explained.',
      targetPercent: 100
    }
  ];

  readonly stagesBn: LoaderStage[] = [
    {
      title: 'AI প্রস্তুতি নিচ্ছে',
      description: 'আপনার ব্যক্তিগত ম্যাচিং মূল্যায়নের প্রস্তুতি চলছে।',
      progress: 'প্রস্তুতি নেওয়া হচ্ছে',
      step: -1,
      footer: 'সঠিক বিশ্লেষণ। স্পষ্ট ধারণা।',
      targetPercent: 0
    },
    {
      title: 'প্রোফাইল পর্যালোচনা করা হচ্ছে',
      description: 'আপনার যোগ্যতা ও অভিজ্ঞতার তথ্য সংগ্রহ করা হচ্ছে।',
      progress: 'প্রোফাইল বোঝা হচ্ছে',
      step: -1,
      footer: 'প্রতিটি তথ্য আপনার উপযুক্ততা যাচাইয়ে সহায়ক।',
      targetPercent: 10
    },
    {
      title: 'শিক্ষাগত যোগ্যতা যাচাই করা হচ্ছে',
      description: 'চাকরির প্রয়োজনীয় শিক্ষাগত যোগ্যতার সাথে তুলনা করা হচ্ছে।',
      progress: 'যোগ্যতা পর্যালোচনা',
      step: 0,
      footer: 'প্রথম বিশ্লেষণ সম্পন্ন হতে চলেছে।',
      targetPercent: 22
    },
    {
      title: 'কাজের স্থান যাচাই করা হচ্ছে',
      description: 'চাকরির লোকেশন আপনার পছন্দের সাথে মিলিয়ে দেখা হচ্ছে।',
      progress: 'স্থান যাচাই',
      step: 1,
      footer: 'মূল্যায়ন রূপ নিচ্ছে।',
      targetPercent: 35
    },
    {
      title: 'বেতন পর্যালোচনা করা হচ্ছে',
      description: 'প্রত্যাশিত বেতন পর্যালোচনা করে দেখা হচ্ছে।',
      progress: 'বেতন পর্যালোচনা',
      step: 2,
      footer: 'বাস্তবসম্মত তথ্য অত্যন্ত গুরুত্বপূর্ণ।',
      targetPercent: 48
    },
    {
      title: 'কাজের অভিজ্ঞতা পর্যালোচনা করা হচ্ছে',
      description: 'আপনার পূর্ব অভিজ্ঞতা চাকরির চাহিদার সাথে মিলিয়ে দেখা হচ্ছে।',
      progress: 'অভিজ্ঞতা যাচাই',
      step: 3,
      footer: 'পদবীর বাইরে অভিজ্ঞতার গভীরতা দেখা হচ্ছে।',
      targetPercent: 62
    },
    {
      title: 'শিল্প খাত যাচাই করা হচ্ছে',
      description: 'শিল্প খাতের অভিজ্ঞতার সামঞ্জস্য খতিয়ে দেখা হচ্ছে।',
      progress: 'শিল্পের সংগতি যাচাই',
      step: 4,
      footer: 'আপনার সামগ্রিক পটভূমি বিবেচনায় নেওয়া হচ্ছে।',
      targetPercent: 74
    },
    {
      title: 'দক্ষতা ও পারদর্শিতা মেলানো হচ্ছে',
      description: 'চাকরির প্রয়োজনীয় দক্ষতার সাথে আপনার মিল খোঁজা হচ্ছে।',
      progress: 'দক্ষতা বিশ্লেষণ',
      step: 5,
      footer: 'আপনার দক্ষতাগুলোর মূল্যায়ন চলছে।',
      targetPercent: 84
    },
    {
      title: 'ম্যাচিং স্কোর হিসাব করা হচ্ছে',
      description: 'সমস্ত তথ্যের ভিত্তিতে চূড়ান্ত স্কোর তৈরি করা হচ্ছে।',
      progress: 'চূড়ান্ত হিসাব চলছে',
      step: 6,
      footer: 'আপনার ফলাফল প্রায় প্রস্তুত।',
      targetPercent: 93
    },
    {
      title: 'আপনার ফলাফল প্রস্তুত',
      description: 'এই পদের সাথে আপনার প্রোফাইলের সামঞ্জস্য নির্ধারণ করা হয়েছে।',
      progress: 'মূল্যায়ন সম্পন্ন',
      step: 7,
      footer: 'আপনার ফলাফল দৃশ্যমান হচ্ছে।',
      targetPercent: 100
    }
  ];

  readonly steps: StepItem[] = [
    { label: 'Education', labelBn: 'শিক্ষাগত যোগ্যতা', icon: '1' },
    { label: 'Location', labelBn: 'স্থান', icon: '2' },
    { label: 'Expected Salary', labelBn: 'প্রত্যাশিত বেতন', icon: '3' },
    { label: 'Experience', labelBn: 'অভিজ্ঞতা', icon: '4' },
    { label: 'Industry alignment', labelBn: 'শিল্প খাত', icon: '5' },
    { label: 'Skills & expertise', labelBn: 'দক্ষতা ও পারদর্শিতা', icon: '6' },
    { label: 'Match insights', labelBn: 'ম্যাচিং বিশ্লেষণ', icon: '7' }
  ];

  private timerId: ReturnType<typeof setInterval> | null = null;
  private startTime = 0;
  private isFinished = false;

  get currentStage(): LoaderStage {
    const list = this.currentLang === 'bn' ? this.stagesBn : this.stagesEn;
    const stage = list[this.currentStageIndex()];
    return stage !== undefined ? stage : list[0];
  }

  getStepStatusText(stepIndex: number, currentStep: number): string {
    const isBn = this.currentLang === 'bn';
    if (stepIndex < currentStep) {
      return isBn ? 'সম্পন্ন' : 'Completed';
    }
    if (stepIndex === currentStep) {
      if (stepIndex === 6) {
        return isBn ? 'প্রস্তুত হচ্ছে' : 'Preparing';
      }
      return isBn ? 'বিশ্লেষণ চলছে' : 'Analyzing';
    }
    return isBn ? 'অপেক্ষমাণ' : 'Waiting';
  }

  ngOnInit(): void {
    if (!this.isBrowser) return;
    this.startTime = performance.now();
    this.runAnimation();
  }

  ngOnDestroy(): void {
    if (this.timerId !== null) {
      clearInterval(this.timerId);
      this.timerId = null;
    }
  }

  private runAnimation(): void {
    const TICK_INTERVAL = 40; // ms
    let currentPercent = 0;

    this.timerId = setInterval(() => {
      if (this.isFinished) return;

      const apiDone = this.isApiComplete();

      if (!apiDone) {
        // Naturally progress up to 94% smoothly
        if (currentPercent < 94) {
          const step = Math.max(0.25, (95 - currentPercent) * 0.03);
          currentPercent = Math.min(94, currentPercent + step);
        }
      } else {
        // API is done! Accelerate swiftly to 100%
        if (currentPercent < 100) {
          const step = Math.max(2.2, (101 - currentPercent) * 0.3);
          currentPercent = Math.min(100, currentPercent + step);
        }
      }

      this.progressPercent.set(Math.floor(currentPercent));
      this.updateStage(currentPercent);

      if (apiDone && currentPercent >= 100) {
        this.isFinished = true;
        if (this.timerId !== null) {
          clearInterval(this.timerId);
          this.timerId = null;
        }
        // Hold on 100% complete for 400ms so user can register the completed state
        setTimeout(() => {
          this.completed.emit();
        }, 400);
      }
    }, TICK_INTERVAL);
  }

  private updateStage(percent: number): void {
    const list = this.currentLang === 'bn' ? this.stagesBn : this.stagesEn;
    if (percent >= 100) {
      this.setStageIndex(list.length - 1);
      return;
    }
    let idx = 0;
    for (let i = 0; i < list.length - 1; i++) {
      const stage = list[i];
      if (stage !== undefined && percent >= stage.targetPercent) {
        idx = i;
      }
    }
    this.setStageIndex(idx);
  }

  private setStageIndex(idx: number): void {
    if (this.currentStageIndex() !== idx) {
      this.currentStageIndex.set(idx);
      this.animatingCopy.set(false);
      setTimeout(() => this.animatingCopy.set(true), 20);
    }
  }
}
